// Angular
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Libraries
import { takeUntil } from 'rxjs';
// Models
import { UnitPeriodChannelDto } from '../../../../../channel/models/unit-period-channel-dto.model';
import { AppCleanup } from '../../../../../../shared/models';
import { JobUpdate, UserUnitPeriodChannelValue } from '../../../../models';
import { UnitPeriodChannelOffsetDto } from '../../../../models/unit-period-channel-offset-dto.model';

// Services
import { JobService } from '../../../../services/job.service';

@Component({
	selector: 'offset-channel-dialog',
	templateUrl: './offset-channel-dialog.component.html',
	styleUrls: ['./offset-channel-dialog.component.scss'],
})
export class OffsetChannelDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string;
	/** Declares if the type of offset the user has input is an Offset or an actual Value */
	offsetSelected: boolean = false;
	/** The raw value that is currently being sent through the live stream, before adjusted */
	displayValue: number;
	/** The value that is currently being displayed for the Channel through Live Data, after being adjusted. Adjustment is from the offsetValue field */
	channelValue: number;
	/** The current offset that is being applied to the Channel Value. Adjustment is from the channelValue field */
	offsetValue: number;
	/** The latest Offset value that has been applied to the Channel for the Job. If no Offset has been applied, this value is 0.*/
	currentOffsetValue: number = 0;
	/** displayValue + currentOffsetValue */
	currentAdjustedValue: number;

	/** Dynamic HTML text to describe what Channel the value is being applied to */
	setValueMessageText: string;
	/** Dynamic HTML text to describe what Channel the offset is being applied to */
	setOffsetMessageText: string;

	/** An array of all Channel Offsets for the Job */
	unitPeriodChannelOffsets: UnitPeriodChannelOffsetDto[] = [];
	/** An array of all Job Channels for the Job */
	unitPeriodChannels: UnitPeriodChannelDto[] = [];

	precision: number = 0;

	constructor(
		public dialogRef: MatDialogRef<OffsetChannelDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { userJobChannelValue: UserUnitPeriodChannelValue; key: string },
		private jobService: JobService
	) {
		super();
	}

	ngOnInit() {
		this.componentTitle = `Edit channel value - ${this.data?.userJobChannelValue?.userUnitPeriodChannel?.unitPeriodChannel?.channel?.label}`;

		this.setValueMessageText = `${this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channel.label} set to`;
		this.setOffsetMessageText = `${this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channel.label} adjusted by`;

		this.jobService.newDataSent$.pipe(takeUntil(this.destroy)).subscribe((result) => {
			result = this.jobService.applyOffsetsToDataPoints(this.data.key, result);
			result = this.jobService.applyPrecisionToDataPoints(this.data.key, result);

			let x = result && result.length > 0 ? result[0] : null;

			if (x && this.data.key === x.k) {
				let channelValue = x.c.find((c) => c.i === this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channelId);

				if (channelValue) {
					this.displayValue = channelValue.rawValue;
				}

				this.precision = this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channel.precision || 0;

				// Depending on the offsetType, one value is locked and the other is dynamically changing, depending on which radio-button is selected.
				if (this.offsetSelected) {
					this.channelValue = Math.round(Math.pow(10, this.precision) * (this.displayValue + this.currentOffsetValue + this.offsetValue)) / Math.pow(10, this.precision);
				} else {
					this.offsetValue = Math.round(Math.pow(10, this.precision) * (this.channelValue - this.displayValue - this.currentOffsetValue)) / Math.pow(10, this.precision);
				}

				this.currentAdjustedValue = Math.round(Math.pow(10, this.precision) * (this.displayValue + this.currentOffsetValue)) / Math.pow(10, this.precision);
			}
		});

		this.jobService
			.getJobStream(String(this.data.key))
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate) {
					let unitPeriodChannelOffsets = this.jobService.findUnitPeriodChannelOffsetsForJob(jobUpdate.job.key);

					this.unitPeriodChannelOffsets = unitPeriodChannelOffsets.filter(
						(unitPeriodChannelOffset) => unitPeriodChannelOffset.unitPeriodChannel.channelId === this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channelId
					);

					let channelOffset = 0;
					if (this.unitPeriodChannelOffsets.length > 0) {
						channelOffset = this.unitPeriodChannelOffsets[this.unitPeriodChannelOffsets.length - 1].offsetValue;
					}

					this.currentOffsetValue = channelOffset;
				}
			});

		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange && jobChange.job.key === this.data.key) {
		// 		let unitPeriodChannelOffsets = this.jobService.findUnitPeriodChannelOffsetsForJob(jobChange.job.key);

		// 		this.unitPeriodChannelOffsets = unitPeriodChannelOffsets.filter(
		// 			(unitPeriodChannelOffset) => unitPeriodChannelOffset.unitPeriodChannel.channelId === this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channelId
		// 		);

		// 		let channelOffset = 0;
		// 		if (this.unitPeriodChannelOffsets.length > 0) {
		// 			channelOffset = this.unitPeriodChannelOffsets[this.unitPeriodChannelOffsets.length - 1].offsetValue;
		// 		}

		// 		this.currentOffsetValue = channelOffset;
		// 	}
		// });
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	/**
	 * Persists Job Channel Offset selection and closes dialog.
	 */
	submit() {
		let offset: number = null;
		if (this.offsetSelected) {
			if (this.offsetValue !== null && this.offsetValue !== undefined) {
				offset = parseFloat(String(this.offsetValue));
			}
		} else {
			offset = this.channelValue - this.displayValue;
		}

		if (this.isFormValid()) {
			let message = '';
			let units = this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channel.units;

			if (this.offsetSelected) {
				message = units ? `${this.setOffsetMessageText} ${this.offsetValue} ${units}` : `${this.setOffsetMessageText} ${this.offsetValue}`;
			} else {
				message = units ? `${this.setValueMessageText} ${this.channelValue} ${units}` : `${this.setValueMessageText} ${this.channelValue}`;
			}

			let unitPeriodChannelOffsetDto: UnitPeriodChannelOffsetDto = {
				id: 0,
				unitPeriodChannelId: this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.id,
				unitPeriodChannel: this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel,
				offsetValue: this.offsetSelected ? offset + this.currentOffsetValue : offset,
				activatedDateUtc: new Date(),
				message: message,
			};

			this.jobService.addJobChannelOffset(this.data.key, unitPeriodChannelOffsetDto);

			this.dialogRef.close();
		}
	}

	isFormValid() {
		let offset: number = null;
		if (this.offsetSelected) {
			if (this.offsetValue == null || this.offsetValue == undefined || Number.isNaN(this.offsetValue)) return false;
			offset = parseFloat(String(this.offsetValue));
		} else {
			if (this.channelValue == null || this.channelValue == undefined || Number.isNaN(this.channelValue)) return false;
			offset = this.channelValue - this.displayValue;
		}

		// return !isNaN(offset) && offset <= 999999999 && ValidationService.regExDecimalMatch(offset, 6);
		return !isNaN(offset) && offset <= 999999999;
	}

	showError() {
		let offset: number = null;
		if (this.offsetSelected) {
			if (this.offsetValue == null || this.offsetValue == undefined || Number.isNaN(this.offsetValue)) return false;
			offset = parseFloat(String(this.offsetValue));
		} else {
			if (this.channelValue == null || this.channelValue == undefined || Number.isNaN(this.channelValue)) return false;
			offset = this.channelValue - this.displayValue;
		}

		// return !isNaN(offset) && (offset > 999999999 || !ValidationService.regExDecimalMatch(offset, 6));
		return !isNaN(offset) && offset > 999999999;
	}

	/**
	 * Calculating the other radio-button input value depending on which value is currently selected
	 */
	calculateOtherValue(type: string) {
		if (type === 'offset' && this.offsetSelected) {
			this.channelValue = Math.round(Math.pow(10, this.precision) * (this.displayValue + this.currentOffsetValue + this.offsetValue)) / Math.pow(10, this.precision);
		} else if (type === 'value' && !this.offsetSelected) {
			this.offsetValue = Math.round(Math.pow(10, this.precision) * (this.channelValue - this.displayValue - this.currentOffsetValue)) / Math.pow(10, this.precision);
		}
	}
}
